import {
  Button,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import ltcpRoutes from "../constants/ltcpRoutes";
import ReactECharts from "echarts-for-react";
import { useContext, useEffect, useState, useRef } from "react";
import { LTCPContext } from "../LTCPContext";
import { getCapacityData, getCapacityFilters } from "../api/ltcpBackend";
import SearchFilter from "LTCP Module/components/SearchFilter";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import DownloadIcon from "@mui/icons-material/Download";
import FilterListIcon from "@mui/icons-material/FilterList";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { useMsal } from "@azure/msal-react";
import { OverviewButton } from "./DashboardPage";
import MultiSearchFilter from "LTCP Module/components/MultiSearchFilter";
import { monthFilterValueConvert } from "utils/filterValueConverter";

const monthNames = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];


// This is a stateless template component to show line charts using
// echarts based on the input labels and data. In the graph the title
// will show the given name on the top of the graph.
function LineChart({ labels, data, name }) {
  const option = {
    grid: {
      top: 25,
      left: 35,
      right: 10,
      bottom: 40,
    },
    tooltip: {
      trigger: "axis",
    },
    xAxis: {
      type: "category",
      data: labels,
      splitLine: {
        show: false,
      },
      axisLabel: {
        textStyle: {
          fontSize: 10,
        },
      },
    },
    yAxis: {
      type: "value",
      splitLine: {
        show: false,
      },
      axisLabel: {
        textStyle: {
          fontSize: 10,
        },
      },
    },
    series: [
      {
        name,
        type: "line",
        data,
        lineStyle: {
          color: "#FFBD65", // Line color
          width: 2, // Line width
        },
        areaStyle: {
          color: "#846CE233", // Fill area with semi-transparent color
        },
        itemStyle: {
          color: "#FFBD65", // Point color
        },
        smooth: true,
        symbolSize: 7,
        symbol: "circle",
      },
    ],
  };

  return (
    <ReactECharts option={option} style={{ width: "100%", height: "100%" }} />
  );
}


// This is a stateless template component to show bar charts using
// echarts based on the input labels and data. In the graph the title
// will show the given name on the top of the graph.
function BarChart({ labels, data, name }) {
  const option = {
    grid: {
      top: 25,
      left: 50,
      right: 20,
      bottom: 40,
    },
    tooltip: {
      trigger: "axis",
    },
    xAxis: {
      type: "category",
      data: labels,
      splitLine: {
        show: false,
      },
      axisLabel: {
        textStyle: {
          fontSize: 10,
        },
      },
    },
    yAxis: {
      type: "value",
      splitLine: {
        show: false,
      },
      axisLabel: {
        textStyle: {
          fontSize: 10,
        },
      },
    },
    series: [
      {
        name,
        type: "bar",
        barWidth: "50%",
        itemStyle: {
          color: "#0055ee",
           borderRadius: [3, 3, 0, 0]
        },
        data,
      },
    ],
  };

  return (
    <ReactECharts option={option} style={{ width: "100%", height: "100%" }} />
  );
}


// This is the capacity page component that is first fetching the data
// based on the default values. After that on every filter change, if all
// the mandatory filters are selected then it will refetch the data from
// the backend api and will refresh the data in all graphs and tables.
function CapacityPage() {
  const navigate = useNavigate();
  const tableRef = useRef();
  const { accounts } = useMsal();
  const activeAccount = accounts[0];
  const userEmail = activeAccount.username;

  const {
    capacityFilters,
    runtime,
    runtype,
    setLoading,
    demandFile,
    capacityDefault,
    commonSelectedFilters,
    setCommonSelectedFilters,
    capacitySelectedFilters,
    setCapacitySelectedFilters,
  } = useContext(LTCPContext);

  const [currentFilters, setCurrentFilters] = useState(capacityFilters);
  const productTypeState =
    capacitySelectedFilters.productTypeState ?? capacityDefault.producttype;
  const cascadeState =
    capacitySelectedFilters.cascadeState ?? capacityDefault.cascade;
  const lineState = capacitySelectedFilters.lineState ?? capacityDefault.line;
  const factoryState = commonSelectedFilters.factoryState === "All"  ? capacityDefault.factory : commonSelectedFilters.factoryState;
  const basepackState = commonSelectedFilters.basepackState === "All" ? capacityDefault.basepack : commonSelectedFilters.basepackState;
  const {
    yearState,
    monthState,
    quarterState,
  } = commonSelectedFilters;
  const commonAppliedFilterNames = [
    "year",
    "quarter",
    "month",
  ];
  const timeGranularity = commonSelectedFilters.timeGranularity || "QoQ";

  const appliedFilters = [
    ...commonSelectedFilters.appliedFilters,
    // ...capacitySelectedFilters.appliedFilters,
  ];

  const setCommonSelectedFilter = (key, value) =>
    setCommonSelectedFilters((prev) => ({ ...prev, [key]: value }));
  const setCapacitySelectedFilter = (key, value) =>
    setCapacitySelectedFilters((prev) => ({ ...prev, [key]: value }));
  const setProductTypeState = (value) =>
    setCapacitySelectedFilter("productTypeState", value);
  const setFactoryState = (value) =>
    setCommonSelectedFilter("factoryState", value);
  const setBasepackState = (value) =>
    setCommonSelectedFilter("basepackState", value);
  const setCascadeState = (value) =>
    setCapacitySelectedFilter("cascadeState", value);
  const setLineState = (value) => setCapacitySelectedFilter("lineState", value);
  const setYearState = (value) => setCommonSelectedFilter("yearState", value);
  const setMonthState = (value) =>
    setCommonSelectedFilter("monthState", value);
  const setQuarterState = (value) =>
    setCommonSelectedFilter("quarterState", value);
  const setTimeGranularity = (value) =>
    setCommonSelectedFilter("timeGranularity", value);
  const setLoadYM = (value) => setCommonSelectedFilter("load_ym", value);
  const setAppliedFilters = (value) => {
    setCommonSelectedFilter(
      "appliedFilters",
      value.filter((filter) => commonAppliedFilterNames.includes(filter))
    );
    // setCapacitySelectedFilter(
    //   "appliedFilters",
    //   value.filter((filter) => !commonAppliedFilterNames.includes(filter))
    // );
  };


  const [tableSortCol, setTableSortCol] = useState("time");
  const [tableSortOrd, setTableSortOrd] = useState(true);
  const [result, setResult] = useState({
    table: [],
    graph: {
      actual_tpm: [],
      bct: [],
      line_oee: [],
      line_speed: [],
      time: [],
    },
  });

  function sortTable(col) {
    if (tableSortCol === col) {
      setTableSortOrd(!tableSortOrd);
    } else {
      setTableSortCol(col);
      setTableSortOrd(true);
    }
  }

  const exportToExcel = () => {
    const table = tableRef.current;
    const worksheet = XLSX.utils.table_to_sheet(table); // Convert table to sheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const dataBlob = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });

    saveAs(dataBlob, "capacity-data.xlsx");
  };

  async function fetchCapacityData() {
    const reloadData =
      productTypeState !== "All" &&
      factoryState !== "All" &&
      basepackState !== "All" &&
      cascadeState !== "All" &&
      lineState !== "All";
    setLoading(true);

    console.log("🚀 ~ fetchCapacityData ~ reloḁdData:", reloadData)
    if (reloadData) {
      const resp = await Promise.all([
        getCapacityData(userEmail, {
          demandFile,
          productTypeState,
          factoryState,
          basepackState,
          cascadeState: cascadeState === "Null" ? "All" : cascadeState,
          lineState,
          yearState,
          monthState: monthFilterValueConvert(monthState),
          quarterState,
          timeGranularity,
          runtime,
          runtype,
        }),
        getCapacityFilters(userEmail, {
          demandFile,
          productTypeState,
          basepackState,
          cascadeState: cascadeState === "Null" ? "All" : cascadeState,
          lineState,
          yearState,
          runtime,
          runtype,
        }),
      ]);

      if (resp[1] === 401) return navigate(ltcpRoutes.HOME);

      let tableData = resp[0].table;
      let graphData = resp[0].graph;

      if (timeGranularity === "MoM") {
        tableData = tableData.map((item) => {
          let month = item.time.split("-");
          month = monthNames[Number.parseInt(month[1]) - 1] + "-" + month[0];

          item.date_col = month;
          return item;
        });

        graphData.time = graphData.time.map((item) => {
          let month = item.split("-");
          month = monthNames[Number.parseInt(month[1]) - 1] + "-" + month[0];

          return month;
        });
      }

      setResult({
        table: tableData,
        graph: graphData,
      });
      setCurrentFilters(resp[1]);

      if (resp[1].cascade.length === 1 && resp[1].cascade[0] === "Null") {
        setCascadeState("Null");
      }
    } else {
      const data = await getCapacityFilters(userEmail, {
        productTypeState,
        factoryState,
        basepackState,
        cascadeState: cascadeState === "Null" ? "All" : cascadeState,
        lineState,
        yearState,
        runtime,
        runtype,
      });

      if (data === 401) return navigate(ltcpRoutes.HOME);

      setCurrentFilters(data);
      if (data.cascade.length === 1 && data.cascade[0] === "Null") {
        setCascadeState("Null");
      }
      setResult({
        table: [],
        graph: {
          actual_tpm: [],
          bct: [],
          line_oee: [],
          line_speed: [],
          time: [],
        },
      });
    }

    setLoading(false);
  }

  function clearFilterHandler() {
    setProductTypeState("All");
    setFactoryState("All");
    if (
      currentFilters.cascade.length === 1 &&
      currentFilters.cascade[0] === "NUll"
    ) {
      setCascadeState("Null");
    } else {
      setCascadeState("All");
    }
    setLineState("All");
    setBasepackState("All");
    setYearState("All");
    setQuarterState("All");
    setMonthState("All");
  }

  useEffect(() => {
    if (capacityFilters.product_type.length === 0)
      return navigate(ltcpRoutes.LANDING);

    fetchCapacityData();
  }, [
    productTypeState,
    factoryState,
    basepackState,
    cascadeState,
    lineState,
    yearState,
    monthState,
    quarterState,
    timeGranularity,
  ]);

  return (
    <Stack p={4} gap={2}>
      <Stack direction="row" gap={2} alignItems="center">
        <OverviewButton
            label="Overview"
            colorFrom="#2563eb"
            colorTo="#1f36c7"
            onClick={() => navigate(ltcpRoutes.DASHBOARD)}
          />
        <OverviewButton
            label="CU%"
            colorFrom="#EC4887"
            colorTo="#B954A4"
            onClick={() => navigate(ltcpRoutes.CU)}
          />
          <OverviewButton
            label="Demand"
            colorFrom="#855FC0"
            colorTo="#5346BA"
            onClick={() => navigate(ltcpRoutes.DEMAND)}
          />
      </Stack>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center">
          {/* <IconButton
            onClick={() => navigate(ltcpRoutes.DASHBOARD)}
            sx={{ color: "#000" }}
          >
            <KeyboardBackspaceIcon />
          </IconButton> */}
          <Typography fontSize={21} fontWeight="bold">
            Capacity Summary
          </Typography>
        </Stack>
        <Button onClick={clearFilterHandler} variant="contained" size="small">
          Clear Filters
        </Button>
      </Stack>
      <Paper>
        <Stack
          py={1}
          direction="row"
          alignItems="center"
          justifyContent="space-evenly"
        >
          <Stack width="11.5%">
            <Typography fontSize={12}>
              Product Type<span style={{ color: "red" }}> *</span>
            </Typography>
            <SearchFilter
              all
              labelSelect
              removeUnderscore
              value={productTypeState}
              setValue={setProductTypeState}
              options={currentFilters.product_type}
            />
          </Stack>
          <Stack width="11.5%">
            <Typography fontSize={12}>
              Factory<span style={{ color: "red" }}> *</span>
            </Typography>
            <SearchFilter
              all
              labelSelect
              value={factoryState}
              setValue={setFactoryState}
              options={currentFilters.factory}
            />
          </Stack>
          <Stack width="11.5%">
            <Typography fontSize={12}>
              Cascade<span style={{ color: "red" }}> *</span>
            </Typography>
            <SearchFilter
              all
              labelSelect
              value={cascadeState}
              setValue={setCascadeState}
              options={currentFilters.cascade}
              disabled={currentFilters.cascade.reduce((acc, curr) => {
                if (curr != "Null") {
                  acc = false;
                }

                return acc;
              }, true)}
            />
          </Stack>
          <Stack width="11.5%">
            <Typography fontSize={12}>
              Line<span style={{ color: "red" }}> *</span>
            </Typography>
            <SearchFilter
              all
              labelSelect
              value={lineState}
              setValue={setLineState}
              options={currentFilters.line}
            />
          </Stack>
          <Stack width="11.5%">
            <Typography fontSize={12}>
              Basepack<span style={{ color: "red" }}> *</span>
            </Typography>
            <SearchFilter
              all
              labelSelect
              value={basepackState}
              setValue={setBasepackState}
              options={currentFilters.basepack}
            />
          </Stack>
          <Stack width="11.5%">
            <Typography fontSize={12}>Year</Typography>
            <Select
              size="small"
              sx={{ height: 26 }}
              value={yearState}
              onChange={(e) => setYearState(e.target.value)}
            >
              <MenuItem value="All">All</MenuItem>
              {currentFilters.year.map((item, index) => (
                <MenuItem key={index} value={item}>
                  {item}
                </MenuItem>
              ))}
            </Select>
          </Stack>
          <Stack width="11.5%">
            <Typography fontSize={12}>Quarter</Typography>
            <MultiSearchFilter
              options={["Q1", "Q2", "Q3", "Q4"]}
              value={quarterState}
              setValue={(v) => {
                setLoadYM(false);
                setQuarterState(v);
              }}
              disabled={timeGranularity === "MoM" || timeGranularity === "YoY"}
              filterName="quarter"
              appliedFilters={appliedFilters}
              setAppliedFilters={setAppliedFilters}
            />
          </Stack>
          <Stack width="11.5%">
            <Typography fontSize={12}>Month</Typography>
            <MultiSearchFilter
              value={monthState}
              setValue={(v) => {
                setLoadYM(false);
                setMonthState(v);
              }}
              options={monthNames}
              disabled={timeGranularity === "QoQ" || timeGranularity === "YoY"}
              filterName="month"
              appliedFilters={appliedFilters}
              setAppliedFilters={setAppliedFilters}
            />
          </Stack>
        </Stack>
      </Paper>
      <Stack
        gap={2}
        direction="row"
        height="30vh"
        minHeight="30vh"
        maxHeight="30vh"
        width="100%"
        minWidth="100%"
        maxWidth="100%"
      >
        <Paper
          sx={{
            p: 1,
            width: "calc(100% - 16px)",
            maxWidth: "calc(100% - 16px)",
            minWidth: "calc(100% - 16px)",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography fontWeight="bold" fontSize={16}>
              Actual Pack Capacity (T)
            </Typography>
            <ToggleButtonGroup
              value={timeGranularity}
              onChange={(_, v) => {
                if (v === null) return;

                setTimeGranularity(v);
                if (v === "YoY") {
                  setMonthState("All");
                  setQuarterState("All");
                } else if (v === "QoQ") {
                  setMonthState("All");
                } else if (v === "MoM") {
                  setQuarterState("All");
                }
              }}
              exclusive
            >
              <ToggleButton value="YoY">YoY</ToggleButton>
              <ToggleButton value="QoQ">QoQ</ToggleButton>
              <ToggleButton value="MoM">MoM</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <BarChart
            labels={result.graph.time}
            data={result.graph.actual_tpm}
            name="Actual Pack Capacity"
          />
        </Paper>
      </Stack>
      <Stack
        gap={2}
        direction="row"
        height="30vh"
        minHeight="30vh"
        maxHeight="30vh"
        width="100%"
        maxWidth="100%"
      >
        <Paper
          sx={{
            p: 1,
            width: "calc(33.1% - 24px)",
            maxWidth: "calc(33.1% - 24px)",
            minWidth: "calc(33.1% - 24px)",
          }}
        >
          <Typography fontWeight="bold" fontSize={16}>
            BCT (Min)
          </Typography>
          <LineChart
            labels={result.graph.time}
            data={result.graph.bct}
            name="BCT"
          />
        </Paper>
        <Paper
          sx={{
            p: 1,
            width: "calc(33.1% - 24px)",
            maxWidth: "calc(33.1% - 24px)",
            minWidth: "calc(33.1% - 24px)",
          }}
        >
          <Typography fontWeight="bold" fontSize={16}>
            Line Speed (UPM)
          </Typography>
          <LineChart
            labels={result.graph.time}
            data={result.graph.line_speed}
            name="Line Speed"
          />
        </Paper>
        <Paper
          sx={{
            p: 1,
            width: "calc(33.1% - 24px)",
            maxWidth: "calc(33.1% - 24px)",
            minWidth: "calc(33.1% - 24px)",
          }}
        >
          <Typography fontWeight="bold" fontSize={16}>
            Line OEE (%)
          </Typography>
          <LineChart
            labels={result.graph.time}
            data={result.graph.line_oee}
            name="Line OEE"
          />
        </Paper>
      </Stack>
      <Paper sx={{ p: 1 }}>
        <Stack direction="row" justifyContent="end" alignItems="center">
          <Tooltip title="Download data" placement="top">
            <IconButton size="small" onClick={exportToExcel}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        <TableContainer sx={{ maxHeight: "20vh", overflowY: "auto" }}>
          <Table ref={tableRef} stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  onClick={() => sortTable("time")}
                  sx={{ background: "#354acd",color:"#fff", textWrap: "nowrap" }}
                >
                  Year
                  <FilterListIcon sx={{ fontSize: 12 }} />
                  {tableSortCol === "time" &&
                    (tableSortOrd ? (
                      <ArrowDownwardIcon sx={{ fontSize: 12 }} />
                    ) : (
                      <ArrowUpwardIcon sx={{ fontSize: 12 }} />
                    ))}
                </TableCell>
                <TableCell
                  onClick={() => sortTable("cascade")}
                  sx={{ background: "#354acd", color:"#fff",textWrap: "nowrap" }}
                >
                  Cascade
                  <FilterListIcon sx={{ fontSize: 12 }} />
                  {tableSortCol === "cascade" &&
                    (tableSortOrd ? (
                      <ArrowDownwardIcon sx={{ fontSize: 12 }} />
                    ) : (
                      <ArrowUpwardIcon sx={{ fontSize: 12 }} />
                    ))}
                </TableCell>
                <TableCell
                  onClick={() => sortTable("bct")}
                  sx={{ background: "#354acd",color:"#fff", textWrap: "nowrap" }}
                >
                  BCT (Min)
                  <FilterListIcon sx={{ fontSize: 12 }} />
                  {tableSortCol === "bct" &&
                    (tableSortOrd ? (
                      <ArrowDownwardIcon sx={{ fontSize: 12 }} />
                    ) : (
                      <ArrowUpwardIcon sx={{ fontSize: 12 }} />
                    ))}
                </TableCell>
                <TableCell
                  onClick={() => sortTable("line")}
                  sx={{ background: "#354acd",color:"#fff", textWrap: "nowrap" }}
                >
                  Unique Line
                  <FilterListIcon sx={{ fontSize: 12 }} />
                  {tableSortCol === "line" &&
                    (tableSortOrd ? (
                      <ArrowDownwardIcon sx={{ fontSize: 12 }} />
                    ) : (
                      <ArrowUpwardIcon sx={{ fontSize: 12 }} />
                    ))}
                </TableCell>
                <TableCell
                  onClick={() => sortTable("basepack")}
                  sx={{ background: "#354acd",color:"#fff", textWrap: "nowrap" }}
                >
                  Basepack
                  <FilterListIcon sx={{ fontSize: 12 }} />
                  {tableSortCol === "basepack" &&
                    (tableSortOrd ? (
                      <ArrowDownwardIcon sx={{ fontSize: 12 }} />
                    ) : (
                      <ArrowUpwardIcon sx={{ fontSize: 12 }} />
                    ))}
                </TableCell>
                <TableCell
                  onClick={() => sortTable("line_speed")}
                  sx={{ background: "#354acd",color:"#fff", textWrap: "nowrap" }}
                >
                  Line Speed (UPM)
                  <FilterListIcon sx={{ fontSize: 12 }} />
                  {tableSortCol === "line_speed" &&
                    (tableSortOrd ? (
                      <ArrowDownwardIcon sx={{ fontSize: 12 }} />
                    ) : (
                      <ArrowUpwardIcon sx={{ fontSize: 12 }} />
                    ))}
                </TableCell>
                <TableCell
                  onClick={() => sortTable("line_oee")}
                  sx={{ background: "#354acd",color:"#fff", textWrap: "nowrap" }}
                >
                  Line OEE %<FilterListIcon sx={{ fontSize: 12 }} />
                  {tableSortCol === "line_oee" &&
                    (tableSortOrd ? (
                      <ArrowDownwardIcon sx={{ fontSize: 12 }} />
                    ) : (
                      <ArrowUpwardIcon sx={{ fontSize: 12 }} />
                    ))}
                </TableCell>
                <TableCell
                  onClick={() => sortTable("act_pack_cap")}
                  sx={{ background: "#354acd", color:"#fff", textWrap: "nowrap" }}
                >
                  Act Pack Cap (T)
                  <FilterListIcon sx={{ fontSize: 12 }} />
                  {tableSortCol === "actual_tpm" &&
                    (tableSortOrd ? (
                      <ArrowDownwardIcon sx={{ fontSize: 12 }} />
                    ) : (
                      <ArrowUpwardIcon sx={{ fontSize: 12 }} />
                    ))}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(tableSortOrd
                ? result.table.sort((a, b) =>
                    typeof a[tableSortCol] === "string"
                      ? a[tableSortCol].localeCompare(b[tableSortCol])
                      : a[tableSortCol] - b[tableSortCol]
                  )
                : result.table.sort((a, b) =>
                    typeof a[tableSortCol] === "string"
                      ? b[tableSortCol].localeCompare(a[tableSortCol])
                      : b[tableSortCol] - a[tableSortCol]
                  )
              ).map((item, index) => (
                <TableRow key={item.time + item.cascade + index}>
                  <TableCell sx={{ textWrap: "nowrap" }}>
                    {timeGranularity === "MoM" ? item.date_col : item.time}
                  </TableCell>
                  <TableCell sx={{ textWrap: "nowrap" }}>
                    {item.cascade}
                  </TableCell>
                  <TableCell sx={{ textWrap: "nowrap" }}>{item.bct}</TableCell>
                  <TableCell sx={{ textWrap: "nowrap" }}>{item.line}</TableCell>
                  <TableCell sx={{ textWrap: "nowrap" }}>
                    {item.basepack}
                  </TableCell>
                  <TableCell sx={{ textWrap: "nowrap" }}>
                    {item.line_speed}
                  </TableCell>
                  <TableCell sx={{ textWrap: "nowrap" }}>
                    {item.line_oee}
                  </TableCell>
                  <TableCell sx={{ textWrap: "nowrap" }}>
                    {item.actual_tpm}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Stack>
  );
}

export default CapacityPage;
